/**
 * useLiveLeave — l'élève quitte le live, avec sa note quand la séance a duré.
 *
 * Les gestes d'aujourd'hui (`StudentControlBar.exit`, `EndSessionScreenStudent`,
 * `LiveSessionDialogComponent`, `SatisfactionScreen`) :
 *  - « Quitter le live » : `POST api/v1/live-sessions/{id}/leave`, puis la sortie du live
 *    (`disconnectFromLive` : le tampon vidé, le mode live éteint, CallKeep arrêté) ;
 *  - la note, au-delà de quinze minutes de séance (`shouldAskRating`) : les étoiles
 *    (`send-satisfaction`, `satisfaction`), puis les chips de cette note (`send-student-comment`,
 *    `student_comment`) que le serveur sert (`satisfaction_student_comments`,
 *    `satisfaction_subtitles`), puis la sortie. « Pas maintenant » sort sans note.
 *
 * Ce qui change : la note et ses chips partent ensemble, au « Terminer » d'une seule feuille, au
 * lieu d'un second écran (`live-satisfaction`) après la sortie ; une sortie refusée par le réseau
 * sort quand même (le serveur clôt la présence à la fin de la journée), au lieu de laisser l'élève
 * bloqué sur un bouton qui tourne ; et la note, ses chips et « quitter » passent par la file de la
 * sortie (`liveExitQueue`) : un trou réseau au moment de partir ne les perd plus, ils repartent à la
 * reprise, l'élève, lui, sort sans les attendre. Vraiment sans : une requête qui pend (un réseau qui
 * ne refuse pas, mais ne répond pas) ne le retient pas plus de `LEAVE_WAIT_MS`.
 *
 * La sortie dit à `onOut` si elle est passée par la note (`afterRating`, donnée ou passée) : le quiz
 * de fin de séance ne se propose qu'à elle. Un `onOut` qui casse se dit, et ne retient pas l'élève.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useDispatch, useSelector, useStore } from "react-redux"

import { apiIdle } from "../../../data/redux/actions/api"
import { liveService } from "../../../data/services/liveService"
import { type EngineTelemetry } from "../../shared/telemetry"
import { useDisconnectFromLive } from "../room/useDisconnectFromLive"
import { enqueueLiveExit } from "./liveExitQueue"

/**
 * Au plus ce délai entre le geste et la sortie. Aucune requête de l'app n'a de délai : sans lui, une
 * requête qui pend gardait l'élève sur « Terminer ». La file d'envoi, elle, continue hors de l'écran.
 */
export const LEAVE_WAIT_MS = 2500

export type LiveRatingState = {
    open: boolean
    rating: number | null
    subtitle: string | undefined
    comments: string[]
    selected: string[]
    submitting: boolean
}

export type LiveLeaveController = {
    leaving: boolean
    /** Quitter, sans note. */
    leave: () => void
    rating: LiveRatingState
    openRating: () => void
    rate: (value: number) => void
    toggleComment: (comment: string) => void
    /** « Terminer » : la note, ses chips, puis la sortie. */
    submitRating: () => void
    /** « Pas maintenant » : la sortie, sans note. */
    dismissRating: () => void
}

/** Une sortie : passée par la note (donnée ou « Pas maintenant »), ou pas (le geste seul). */
export type LiveExit = { afterRating: boolean }

export type LiveLeaveOptions = {
    /**
     * Part au geste, avant la sortie qui vide le journal de la séance : ce qui doit encore s'y écrire
     * (la coupure en cours) s'y écrit.
     */
    beforeLeave?: () => void
    /** Part avec la sortie, au moment où le live se quitte. */
    onOut?: (exit: LiveExit) => void
}

type RootState = {
    data?: {
        staticData?: {
            satisfaction_student_comments?: Record<string, string[]> | null
            satisfaction_subtitles?: Record<string, string> | null
        }
    }
}

export function useLiveLeave(
    liveSessionId: number | null,
    telemetry: EngineTelemetry,
    options: LiveLeaveOptions = {}
): LiveLeaveController {
    const dispatch = useDispatch()
    const { getState } = useStore()
    const { disconnectFromLive } = useDisconnectFromLive() as { disconnectFromLive: () => Promise<void> }
    const commentsByRating = useSelector((state: RootState) => state.data?.staticData?.satisfaction_student_comments)
    const subtitles = useSelector((state: RootState) => state.data?.staticData?.satisfaction_subtitles)

    const [leaving, setLeaving] = useState(false)
    const [open, setOpen] = useState(false)
    const [rating, setRating] = useState<number | null>(null)
    const [selected, setSelected] = useState<string[]>([])
    /** La note déjà confiée à la file, au toucher de l'étoile. */
    const queuedRatingRef = useRef<number | null>(null)

    const optionsRef = useRef(options)
    optionsRef.current = options
    const outTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    useEffect(
        () => () => {
            if (outTimerRef.current != null) clearTimeout(outTimerRef.current)
        },
        []
    )

    const go = useCallback(
        (afterRating: boolean) => {
            if (liveSessionId == null || leaving) return
            setLeaving(true)
            setOpen(false)
            optionsRef.current.beforeLeave?.()
            let gone = false
            const out = () => {
                if (gone) return
                gone = true
                if (outTimerRef.current != null) clearTimeout(outTimerRef.current)
                outTimerRef.current = null
                try {
                    optionsRef.current.onOut?.({ afterRating })
                } catch (cause: unknown) {
                    telemetry.error("leave_on_out", cause)
                }
                disconnectFromLive().catch((cause: unknown) => {
                    telemetry.error("disconnect_from_live", cause)
                })
                dispatch(apiIdle(liveService.sendSatisfaction) as never)
                dispatch(apiIdle(liveService.sendStudentComment) as never)
            }
            // L'élève sort au premier essai, parti ou gardé pour la reprise, ou au délai si la requête pend :
            // il n'attend pas le réseau.
            outTimerRef.current = setTimeout(() => {
                telemetry.transition("leaving", "left", "leave_wait_elapsed")
                out()
            }, LEAVE_WAIT_MS)
            void enqueueLiveExit(dispatch as never, getState, { kind: "leave_session", liveSessionId }).then(out)
        },
        [liveSessionId, leaving, dispatch, getState, disconnectFromLive, telemetry]
    )
    const leave = useCallback(() => go(false), [go])

    // Une note ne part qu'une fois : la redonner effacerait les chips qui la suivent.
    const queueRating = useCallback(
        (value: number) => {
            if (liveSessionId == null || queuedRatingRef.current === value) return
            queuedRatingRef.current = value
            void enqueueLiveExit(dispatch as never, getState, {
                kind: "satisfaction",
                liveSessionId,
                data: { satisfaction: value },
            })
        },
        [liveSessionId, dispatch, getState]
    )

    const submitRating = useCallback(() => {
        if (liveSessionId == null || rating == null) return
        // Dans l'ordre de la file : la note, ses chips (le serveur les efface à chaque note), puis la sortie.
        // La note est partie au toucher de l'étoile.
        queueRating(rating)
        if (selected.length > 0) {
            void enqueueLiveExit(dispatch as never, getState, {
                kind: "student_comment",
                liveSessionId,
                data: { student_comment: selected },
            })
        }
        go(true)
    }, [liveSessionId, rating, selected, dispatch, getState, go, queueRating])

    const comments = useMemo(
        () => (rating != null ? (commentsByRating?.[String(rating)] ?? []) : []),
        [commentsByRating, rating]
    )

    return {
        leaving,
        leave,
        rating: {
            open,
            rating,
            subtitle: rating != null ? (subtitles?.[String(rating)] ?? undefined) : undefined,
            comments,
            selected,
            submitting: leaving,
        },
        openRating: () => setOpen(true),
        rate: (value) => {
            setRating(value)
            // Les chips d'une autre note ne valent pas pour celle-ci, comme aujourd'hui.
            setSelected([])
            // L'étoile touchée part tout de suite, comme l'ancienne note (`SatisfactionComponent`) :
            // l'élève qui ferme ensuite sans « Terminer » a quand même donné son avis.
            queueRating(value)
        },
        toggleComment: (comment) =>
            setSelected((list) =>
                list.includes(comment) ? list.filter((item) => item !== comment) : [...list, comment]
            ),
        submitRating,
        dismissRating: () => go(true),
    }
}
